"use client";
/*
 * Documentation:
 * Button — https://app.subframe.com/4f62853ee923/library?component=Button_3b777358-b86b-40af-9327-891efc6826fe
 * DC_Card_Person — https://app.subframe.com/4f62853ee923/library?component=DC_Card_Person_1574d499-1fd3-4aec-890b-0b2e114659f0
 */

import React from "react";
import { FeatherArrowUpRight } from "@subframe/core";
import * as SubframeUtils from "../utils";
import { Button } from "./Button";

interface ProfileButtonProps extends React.ComponentProps<typeof Button> {
  children?: React.ReactNode;
  className?: string;
}

const ProfileButton = React.forwardRef<
  React.ElementRef<typeof Button>,
  ProfileButtonProps
>(function ProfileButton(
  { children, className, ...otherProps }: ProfileButtonProps,
  ref
) {
  return (
    <Button
      className={className}
      variant="neutral-primary"
      iconRight={<FeatherArrowUpRight />}
      ref={ref}
      {...otherProps}
    >
      {children}
    </Button>
  );
});

interface Dc_Card_PersonRootProps extends React.HTMLAttributes<HTMLDivElement> {
  isMarginal?: boolean;
  name?: React.ReactNode;
  subtext?: React.ReactNode;
  text?: React.ReactNode;
  imageSlot?: React.ReactNode;
  buttonSlot?: React.ReactNode;
  imageCredits?: React.ReactNode;
  buttonText?: React.ReactNode;
  noText?: boolean;
  isPrint?: boolean;
  children?: React.ReactNode;
  className?: string;
}

const Dc_Card_PersonRoot = React.forwardRef<
  HTMLDivElement,
  Dc_Card_PersonRootProps
>(function Dc_Card_PersonRoot(
  {
    isMarginal = false,
    name,
    subtext,
    text,
    imageSlot,
    buttonSlot,
    imageCredits,
    buttonText,
    noText = false,
    isPrint = false,
    children,
    className,
    ...otherProps
  }: Dc_Card_PersonRootProps,
  ref
) {
  return (
    <div
      className={SubframeUtils.twClassNames(
        "group/1574d499 flex w-full flex-col items-start gap-6 rounded-md bg-neutral-50 px-6 py-6",
        { "px-2 py-2": isPrint, "px-4 py-4": isMarginal },
        className
      )}
      ref={ref}
      {...otherProps}
    >
      <div
        className={SubframeUtils.twClassNames(
          "flex w-full items-center gap-4",
          { "flex-col flex-nowrap items-start justify-start gap-4": isMarginal }
        )}
      >
        {imageSlot ? (
          <div className="flex h-24 w-24 flex-none items-center gap-4 overflow-hidden rounded-full relative">
            {imageSlot}
          </div>
        ) : null}
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
          {name ? (
            <span className="w-full text-heading-3 font-heading-3 text-default-font">
              {name}
            </span>
          ) : null}
          {subtext ? (
            <span className="w-full text-caption font-caption text-subtext-color">
              {subtext}
            </span>
          ) : null}
        </div>
      </div>
      {children ? (
        <div className="flex flex-col items-start">{children}</div>
      ) : null}
      {buttonSlot ? (
        <div className="flex w-full items-center justify-between">
          {buttonSlot}
        </div>
      ) : null}
    </div>
  );
});

export const Dc_Card_Person = Object.assign(Dc_Card_PersonRoot, {
  ProfileButton,
});
